import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  createAddCardSessionRequest,
  parseCollectionMethodPatchResponse,
  patchSubscriptionCollectionMethodRequest,
} from 'services/subscriptions';
import { getCurrentUserSubscription } from 'store/actionCreators/subscriptions';
import { UserSubscription } from 'types';
import { getPaymentMethodSummary } from 'utils/subscriptionBilling';
import { parseStripePaymentMethod } from 'utils/stripePaymentMethod';
import { toast as addAppToast } from 'utils/toastAbstraction';

import { SwitchBillingModeError, TextLink } from './styled';
import SwitchBillingModeModal, {
  type SwitchBillingModeStep,
} from './SwitchBillingModeModal';

type SwitchBillingModeLinkProps = {
  subscription: UserSubscription;
};

const SwitchBillingModeLink = ({
  subscription,
}: SwitchBillingModeLinkProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState<SwitchBillingModeStep>('confirm');
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [pendingRedirectUrl, setPendingRedirectUrl] = useState<string | null>(
    null,
  );

  const isInvoiceBilling =
    subscription.stripeCollectionMethod === 'send_invoice';
  const targetMethod = isInvoiceBilling ? 'card' : 'invoice';

  const cardBrand = useMemo(() => {
    const fromStripe = parseStripePaymentMethod(subscription.stripePayment)
      ?.card?.brand;
    if (fromStripe) return fromStripe;
    const legacy = subscription.paymentMethodBrand;
    return typeof legacy === 'string' && legacy.trim() ? legacy.trim() : null;
  }, [subscription.paymentMethodBrand, subscription.stripePayment]);

  const cardSummary = useMemo(
    () => getPaymentMethodSummary(subscription),
    [subscription],
  );

  const linkLabel = useMemo(
    () =>
      isInvoiceBilling
        ? t('subscriptions.profile.payment.switchToCard')
        : t('subscriptions.profile.payment.switchToInvoice'),
    [isInvoiceBilling, t],
  );

  const resetModal = useCallback(() => {
    setStep('confirm');
    setSubmitting(false);
    setRedirecting(false);
    setInlineError(null);
    setPendingRedirectUrl(null);
  }, []);

  const openModal = useCallback(() => {
    resetModal();
    setModalOpen(true);
  }, [resetModal]);

  const closeModal = useCallback(() => {
    if (submitting || redirecting) return;
    setModalOpen(false);
    resetModal();
  }, [redirecting, resetModal, submitting]);

  const handleConfirm = useCallback(async () => {
    if (submitting || redirecting) return;
    setInlineError(null);
    setSubmitting(true);
    try {
      const data = await patchSubscriptionCollectionMethodRequest({
        method: targetMethod,
      });
      const { addCardRequired, redirectUrl } =
        parseCollectionMethodPatchResponse(data);

      if (addCardRequired) {
        setPendingRedirectUrl(redirectUrl);
        setStep('add-card');
        return;
      }

      setModalOpen(false);
      resetModal();
      addAppToast(
        { title: t('notifications.billing_mode_updated') },
        {
          appearance: 'success',
          autoDismiss: true,
        },
      );
      dispatch(getCurrentUserSubscription.trigger());
    } catch {
      setInlineError(
        t('subscriptions.profile.payment.switchBillingErrorGeneric'),
      );
    } finally {
      setSubmitting(false);
    }
  }, [dispatch, redirecting, resetModal, submitting, t, targetMethod]);

  const handleGoAddCard = useCallback(async () => {
    if (submitting || redirecting) return;
    setInlineError(null);
    setRedirecting(true);
    try {
      const redirectUrl =
        pendingRedirectUrl ||
        (await createAddCardSessionRequest().then((session) => session.url));
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }
      setInlineError(
        t('subscriptions.profile.payment.switchBillingErrorGeneric'),
      );
      setRedirecting(false);
    } catch {
      setInlineError(
        t('subscriptions.profile.payment.switchBillingErrorGeneric'),
      );
      setRedirecting(false);
    }
  }, [pendingRedirectUrl, redirecting, submitting, t]);

  return (
    <>
      <div>
        <TextLink
          as="button"
          type="button"
          onClick={openModal}
          style={{
            fontSize: 11,
            marginTop: 6,
            padding: 0,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            display: 'inline-block',
          }}
        >
          {linkLabel}
        </TextLink>
        {!modalOpen && inlineError ? (
          <SwitchBillingModeError role="alert">
            {inlineError}
          </SwitchBillingModeError>
        ) : null}
      </div>

      <SwitchBillingModeModal
        isOpen={modalOpen}
        isInvoiceBilling={isInvoiceBilling}
        step={step}
        submitting={submitting}
        redirecting={redirecting}
        error={modalOpen ? inlineError : null}
        cardBrand={cardBrand}
        cardSummary={cardSummary}
        onClose={closeModal}
        onConfirm={handleConfirm}
        onGoAddCard={handleGoAddCard}
      />
    </>
  );
};

export default SwitchBillingModeLink;
